import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import toast from 'react-hot-toast';

// Completa (o corrige) la persona que entrega y la fecha de despacho de un
// cambio ya aprobado. Sirve en cualquier momento: antes de la guía, junto con
// ella o mucho después. No toca el número de guía.
export default function CompletarEntregaModal({
  isOpen,
  onClose,
  registros = [],
  personalEmpresa = [],
  onListo,
}) {
  const [quienEntrega, setQuienEntrega] = useState('');
  const [fechaDespacho, setFechaDespacho] = useState('');
  const [guardando, setGuardando] = useState(false);

  const lista = registros || [];
  const primero = lista[0] || null;
  const varios = lista.length > 1;

  useEffect(() => {
    if (!isOpen) return;
    // Se precarga lo que ya tenga cualquiera de los tóners del cambio
    setQuienEntrega(lista.map((r) => r.entregado_por).find(Boolean) || '');
    setFechaDespacho(lista.map((r) => r.fecha_despacho).find(Boolean) || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, primero?.id]);

  const guardar = async (e) => {
    e.preventDefault();

    if (!quienEntrega && !fechaDespacho) {
      toast.error('Completa al menos uno de los dos datos');
      return;
    }

    const ids = lista.map((r) => r.id).filter((id) => id !== null && id !== undefined);
    if (!ids.length) {
      toast.error('No hay registros para actualizar');
      return;
    }

    // Solo se mandan los que tienen valor: dejar uno en blanco no borra
    // lo que ya estaba guardado.
    const cambios = {};
    if (quienEntrega) cambios.entregado_por = quienEntrega;
    if (fechaDespacho) cambios.fecha_despacho = fechaDespacho;

    setGuardando(true);
    const { error } = await supabase.from('registros').update(cambios).in('id', ids);
    setGuardando(false);

    if (error) {
      console.error('Error al completar los datos de entrega:', error);
      toast.error(
        String(error.message || '').includes('fecha_despacho')
          ? 'Falta ejecutar el script 20 en la base de datos (columna fecha_despacho).'
          : 'No se pudieron guardar los datos: ' + (error.message || 'error desconocido')
      );
      return;
    }

    toast.success(varios ? `Datos guardados en los ${lista.length} tóners` : 'Datos guardados');
    onListo?.(ids, cambios);
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-5 sm:p-6 rounded-lg shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold mb-2 text-gray-800">Datos de entrega</h2>

        {primero && (
          <p className="text-sm text-gray-600 mb-4">
            {primero.clientes?.nombre ? `${primero.clientes.nombre} · ` : ''}
            <span className="font-mono">{primero.serie_maquina}</span>
            {varios && (
              <span className="block mt-1 font-medium text-gray-700">
                Se guarda en los {lista.length} tóners: {lista.map((r) => r.color).join(', ')}
              </span>
            )}
            {primero.guia && (
              <span className="block mt-1">
                Guía: <span className="font-semibold text-gray-800">{primero.guia}</span>
              </span>
            )}
          </p>
        )}

        <form onSubmit={guardar}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Persona encargada de la entrega
            </label>
            <select
              value={quienEntrega}
              onChange={(e) => setQuienEntrega(e.target.value)}
              className="w-full border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              disabled={guardando}
            >
              <option value="">-- Sin definir --</option>
              {personalEmpresa.map((persona) => (
                <option key={persona.id} value={persona.id}>{persona.nombre}</option>
              ))}
            </select>
          </div>

          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Será despachado
            </label>
            <input
              type="date"
              value={fechaDespacho}
              onChange={(e) => setFechaDespacho(e.target.value)}
              className="w-full border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={guardando}
            />
          </div>

          <p className="text-xs text-gray-500 mb-5">
            Puedes guardar solo uno de los dos y completar el otro más adelante.
            Lo que dejes en blanco se mantiene como estaba.
          </p>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
              disabled={guardando}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400"
              disabled={guardando}
            >
              {guardando ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
